import React, { useMemo, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";
import ThemeableAgGrid from "../../../common/components/agGrid/ThemeableAgGrid";
import { useTranslation } from "react-i18next";

const CurrenciesTable = ({
  currencies,
  onSort,
  onRowClick,
  onPaginationChanged,
  paginationState,
}) => {
  const { t } = useTranslation(["markets"]);

  // Memoize column definitions
  const columnDefs = useMemo(
    () => [
      {
        field: "name",
        headerName: t("CurrencyName"),
        sortable: true,
        filter: true,
        // Good for name/string columns
        flex: 1,
      },
      {
        field: "symbol",
        headerName: t("CurrencySymbol"),
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: "active",
        headerName: t("Active"),
        sortable: true,
        filter: true,
        flex: 1,
        headerClass: "ag-center-aligned-header",
        cellRenderer: (params) => {
          const isActive = params.data?.active;
          return (
            <div className="centered" style={{ width: "100%", height: "100%" }}>
              <FontAwesomeIcon
                icon={isActive ? faCheck : faTimes}
                className={isActive ? "text-success" : "text-danger"}
                style={{ fontSize: "16px" }}
              />
            </div>
          );
        },
      },
    ],
    [t]
  );

  // Memoize default column properties
  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      minWidth: 100,
      sortable: true,
      filter: true,
    }),
    []
  );

  // Handle sort changes to maintain consistent sorting API
  const handleSortChanged = (event) => {
    if (!onSort) return;

    const sortModel = event.api.getSortModel();
    if (sortModel.length > 0) {
      const { colId, sort } = sortModel[0];
      onSort(colId, sort);
    } else {
      onSort(null, null);
    }
  };

  // Pagination configuration
  const paginationConfig = useMemo(
    () => ({
      // Enable pagination for all tables regardless of size
      enabled: true,
      // Use the page size from props or default
      pageSize: paginationState?.pageSize || 20,
      // Available page size options for user to choose
      pageSizeOptions: [5, 10, 20, 50, 100],
    }),
    [paginationState?.pageSize]
  );

  // Handle pagination changes
  const handlePaginationChanged = useCallback(
    (params) => {
      if (onPaginationChanged && params) {
        onPaginationChanged(params);
      }
    },
    [onPaginationChanged]
  );

  // Grid ready handler
  const onGridReady = useCallback(
    (params) => {
      // Set current page from external state if available
      if (paginationState && paginationState.currentPage >= 0) {
        setTimeout(() => {
          params.api.paginationGoToPage(paginationState.currentPage);
        }, 0);
      }
    },
    [paginationState]
  );

  return (
    <ThemeableAgGrid
      className="table-bold clickable"
      columnDefs={columnDefs}
      defaultColDef={defaultColDef}
      rowData={currencies}
      onSortChanged={onSort ? handleSortChanged : undefined}
      onRowClicked={(params) => onRowClick && onRowClick(params.data)}
      onPaginationChanged={handlePaginationChanged}
      onGridReady={onGridReady}
      getRowId={(params) => params.data.currencyId.toString()}
      animateRows={true}
      // Ensure minimum row height for better alignment of icons
      rowHeight={40}
      // Always use normal layout
      domLayout="autoHeight"
      // Enable pagination
      pagination={true}
      // Configure pagination settings
      paginationPageSize={paginationConfig.pageSize}
      paginationPageSizeSelector={paginationConfig.pageSizeOptions}
      // Enable panel to show entries info
      suppressPaginationPanel={false}
    />
  );
};

export default CurrenciesTable;

